/* Steve.js */

"use strict";
const namespace = "Steve.$teve";
const SINGLETON_KEY = Symbol.for(namespace);
const globalSpace = typeof window === "undefined" ? global : window;
const globalSymbols = Object.getOwnPropertySymbols(globalSpace);

const EventEmitter = require("events").EventEmitter;
const _ = require("lodash");
_.mixin(require("underscore.string").exports());
const Di = require("@gluenetworks/steve_core_dependency_injector");
const CoreLogger = require("@gluenetworks/steve_core_logger");
const Router = require("@gluenetworks/steve_core_router");
const SteveUtils = require("@gluenetworks/steve_core_utils");
const SteveIo = require("@gluenetworks/steve_core_io");

/**
 * @typedef AddonInitializeFunction
 * @type {function}
 * @description This function is called to initialized the previously created Steve Add on. This function's this
 *  context is the previously created Steve Add on instance.
 * @param {Steve} steve - The Steve singleton instance.
 * @param {Object} addonOptions - The options object that was passed into the Steve.addOn method.
 */
/**
 * @typedef AddonStartUpFunction
 * @type {function}
 * @description This function is called to start up the previously created Steve Add on. This function's this
 *  context is the previously created Steve Add on instance.
 * @param {Steve} steve - The Steve singleton instance.
 * @param {Object} addonOptions - The options object that was passed into the Steve.addOn method.
 */

/*
TODO Steve Options
 */

class Steve extends EventEmitter {
    constructor() {
        super();
        //Root DI
        this._di = new Di();

        //Logger
        this._logger = CoreLogger.getConsoleLogger("steve", "info");
        this._di.registry.register("$teve.logger", this._logger);

        //Router
        this._router = new Router(globalSpace, globalSpace.history, this._logger, {
            encodeRouteData: true,
        }); //<---Keeping the Route Data in plain text for Dev Puposes.
        this._di.registry.register("$teve.router", this._router);

        //IO
        this._io = new SteveIo();
        this._di.registry.register("$teve.io", this._io);

        //utils
        this._utils = SteveUtils;
        this._di.registry.register("$teve.utils", this._utils);

        //addons
        this._registeredAddOns = {};

        //Make steve accessible
        globalSpace.steve = this;
        globalSpace.Steve = this;
        this._di.registry.register("$teve.core", this);
    }
    /*******************************************/
    /* START PRIVATE API METHODS */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE API METHODS */
    /* START PUBLIC MEMBERS */
    /*******************************************/
    get di() {
        return this._di;
    }
    get logger() {
        return this._logger;
    }
    get router() {
        return this._router;
    }
    get utils() {
        return this._utils;
    }
    get io() {
        return this._io;
    }
    /*******************************************/
    /* END PUBLIC MEMBERS */
    /* START PRIVATE API METHODS */
    /*******************************************/
    _initialize() {
        let addOnNames = Object.keys(this._registeredAddOns);
        let allPromise = SteveUtils.promise.serial(
            addOnNames,
            (allProm, registeredAddOnName /* ,currentIndex */) => {
                //jshint ignore:line
                let addOnDetails = this._registeredAddOns[registeredAddOnName];
                if (addOnDetails && _.isFunction(addOnDetails.initialize)) {
                    return allProm
                        .then(() => {
                            return addOnDetails.initialize.call(
                                addOnDetails.instance,
                                this,
                                addOnDetails.options
                            );
                        })
                        .then(() => {
                            this.emit("AddonInitialized", addOnDetails);
                        });
                } else {
                    return undefined;
                }
            }
        );
        return allPromise;
    }
    _startUp() {
        let addOnNames = Object.keys(this._registeredAddOns);
        let allPromise = SteveUtils.promise.serial(
            addOnNames,
            (allProm, registeredAddOnName /* ,currentIndex */) => {
                //jshint ignore:line
                let addOnDetails = this._registeredAddOns[registeredAddOnName];
                if (addOnDetails && _.isFunction(addOnDetails.startUp)) {
                    return allProm
                        .then(() => {
                            return addOnDetails.startUp.call(
                                addOnDetails.instance,
                                this,
                                addOnDetails.options
                            );
                        })
                        .then(() => {
                            this.emit("AddonStartupCompleted", addOnDetails);
                        });
                } else {
                    return undefined;
                }
            }
        );
        return allPromise;
    }
    /*******************************************/
    /* END PRIVATE MEMBERS */
    /* START PUBLIC API METHODS */
    /*******************************************/
    static getInstance() {
        return globalSpace[SINGLETON_KEY];
    }

    /**
     * Install a Steve Core Add-on
     * @param {string} name - The name of the the thing to be installed.
     * @param {object} thing - The thing to be installed.
     * @param {object} [opts] - The install options
     * @param {boolean} [opts.noCreationMode=false] - If true will treat thing as an already created instance,
     * @param {string} [opts.registrationFqn = $teve.<lowerCamelCaseName>] - if a string is provided then it will be used instead of the standard $teve.<lowerCamelCaseName>
     *  else it will use di to create an instance of thing.
     * @param {boolean} [opts.addOnMode=false] - If true assumes the thing has built in initialize and startUp methods.
     * @param {AddonInitializeFunction} [opts.initialize] - If addOnMode is false and a function is provided it will be called during the initialization phase.
     * @param {AddonStartUpFunction} [opts.startUp] - If addOnMode is false and a function is provided it will be called during the startUp phase.
     */
    addOn(name, thing, opts) {
        let createdThing;
        let lowerCamelCaseName = _.camelize(name, true);
        let registeredFqn = _.get(opts, "registrationFqn", `$teve.${lowerCamelCaseName}`);
        let registeredDetails = this._di.registry.getNameAndNamespace(
            registeredFqn,
            "Can't register an addon without a namespace."
        );
        //create the thing
        if (_.get(opts, "noCreationMode", false)) {
            createdThing = thing;
        } else {
            createdThing = this._di.create(thing, {
                injectableOverrides: {
                    "$teve.registeredDetails": registeredDetails,
                },
            });
        }
        //create the registered Add Ons object
        this._registeredAddOns[lowerCamelCaseName] = {
            name: name,
            instance: createdThing,
            options: opts,
        };
        //If we are in addOnMode then the initialize/startUp function are provided in the createdThing
        if (_.get(opts, "addOnMode", false)) {
            this._registeredAddOns[lowerCamelCaseName].initialize = createdThing.initialize;
            this._registeredAddOns[lowerCamelCaseName].startUp = createdThing.startUp;
        } else {
            this._registeredAddOns[lowerCamelCaseName].initialize = _.get(opts, "initialize");
            this._registeredAddOns[lowerCamelCaseName].startUp = _.get(opts, "startUp");
        }
        //Expose this Add On in the Registry
        this._di.registry.register(registeredFqn, createdThing);

        //Also expose this directly on the steve object
        Object.defineProperty(this, lowerCamelCaseName, {
            get: () => {
                let found = this._di.registry.getRegistered(registeredFqn);
                if (found) {
                    found = found.registeredObj;
                }
                return found;
            },
        });
        this.emit("AddonRegistered", this._registeredAddOns[lowerCamelCaseName], {
            registeredFqn: registeredFqn,
            steveCorePropertyName: lowerCamelCaseName,
        });
    }
    launchApp(launchOpts) {
        return new Promise((resolve, reject) => {
            this._initialize().then(
                (initializationResults) => {
                    this._logger.info(
                        {initializationResults: initializationResults},
                        "STEVE Initialization phase Completed."
                    );
                    this._startUp().then(
                        (startUpResults) => {
                            this._logger.info(
                                {startUpResults: startUpResults},
                                "STEVE Startup phase Completed."
                            );
                            this._registeredAddOns = {};
                            this.emit("SteveStartupCompleted", launchOpts);
                            resolve();
                        },
                        (startingUpFailedResults) => {
                            this._logger.fatal(
                                {startingUpFailedResults: startingUpFailedResults},
                                "STEVE Startup phase FAILED."
                            );
                            reject(startingUpFailedResults);
                        }
                    );
                },
                (initializeFailedResults) => {
                    this._logger.fatal(
                        {initializeFailedResults: initializeFailedResults},
                        "STEVE Initialize phase FAILED."
                    );
                    reject(initializeFailedResults);
                }
            );
        });
    }
    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}

//If this is the first time go ahead and create the symbol.
if (globalSymbols.indexOf(SINGLETON_KEY) === -1) {
    globalSpace[SINGLETON_KEY] = new Steve();
}

module.exports = Steve;
